function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useEffect, useRef } from 'react';
import Downshift from 'downshift';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
/** @jsxRuntime classic */

/** @jsx jsx */

import { jsx, css } from '@emotion/core';
import { CaretDownIcon } from '../../../icon';
import { home, migratoryWebCitizen, phytosanitaryWeb, sanitarianRegistersWeb, tdCarWeb } from '../../Colors';
/**
 * This component represent a drop down.
 * @param {Array} items Array with the dropdown options.
 * @param {string} placeholder Text that will be shown when the user has not type into the dropdown.
 * @param {function} onChange Function triggered when the selected value in the dropdown changes.
 * @param {JSON} style Container custom style for the dropdown.
 * @param {JSON} inputStyle Style for the dropdown.
 * @param {Object} dropDownRef Dropdown ref.
 * @param {boolean} disabled Determine if the style is disabled or enabled.
 * @param {boolean} alternative Determine if the style is the alternative (red) or the normal (blue) arrow.
 * @param {*} value Dropdown value.
 * @param {string} id Dropdown id.
 * @param {*} rest All other supported props.
 */

const DropDownCombo = ({
  items,
  placeholder,
  onChange,
  style: containerStyle = {},
  inputStyle,
  dropDownRef = null,
  disabled = false,
  alternative = false,
  value = '',
  id = '',
  ...rest
}) => {
  const [defaultValue, setValue] = useState('');
  const {
    error,
    touched
  } = rest;
  const inputRef = useRef();
  useEffect(() => {
    setValue(value);
  }, [value]);
  return jsx("div", {
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, jsx(Downshift, {
    onChange: (selection, {
      reset
    }) => {
      reset();
      inputRef.current.blur();
      return selection !== null && selection.id !== null ? onChange(selection) : onChange('');
    } // eslint-disable-next-line react/jsx-curly-newline
    ,
    itemToString: itemToString,
    ref: dropDownRef,
    selectedItem: defaultValue || ''
  }, ({
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    isOpen,
    clearSelection,
    selectedItem,
    highlightedIndex,
    inputValue,
    toggleMenu
  }) => {
    const inputOptions = {
      isOpen,
      placeholder,
      disabled,
      error,
      touched,
      id,
      onClick: () => toggleMenu()
    };

    if (selectedItem) {
      const findItem = items.find(x => x.id === selectedItem.id);

      if (findItem && selectedItem.name !== findItem.name) {
        inputOptions.value = findItem.name;
      }
    }

    if (defaultValue !== '' && defaultValue !== undefined && !isOpen) {
      const findItem = items.find(x => x.id === defaultValue || x.name === defaultValue);

      if (findItem) {
        inputOptions.value = findItem.name;
      } else {
        inputOptions.value = defaultValue;
      }
    }

    const inputProps = getInputProps(inputOptions);
    return jsx("div", {
      style: {
        width: 250,
        margin: 'auto',
        ...containerStyle,
        border: 'none'
      }
    }, jsx("div", {
      style: {
        position: 'relative'
      }
    }, jsx(Input, _extends({
      ref: inputRef
    }, inputProps, {
      onKeyDown: evt => {
        if (evt.key === 'Enter') {
          evt.stopPropagation();
          evt.preventDefault();
        }

        inputProps.onKeyDown(evt);
      },
      style: {
        width: 250,
        margin: 'auto',
        ...containerStyle,
        ...inputStyle
      }
    })), selectedItem && selectedItem !== '' ? jsx(ControllerButton, _extends({}, getToggleButtonProps(), {
      onClick: clearSelection,
      "aria-label": "clear selection",
      disabled: disabled
    }), jsx(XIcon, {
      disabled: disabled,
      alternative: alternative
    })) : jsx(ControllerButton, _extends({}, getToggleButtonProps(), {
      disabled: disabled
    }), jsx(ArrowIcon, {
      isOpen: isOpen,
      disabled: disabled,
      alternative: alternative
    }))), jsx("div", {
      style: {
        position: 'relative'
      }
    }, jsx(Menu, getMenuProps({
      isOpen
    }), isOpen ? items.filter(i => {
      if (inputValue && inputValue !== defaultValue) {
        const searchValue = inputValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
        const arrayValue = i.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        return !searchValue || arrayValue.includes(searchValue);
      }

      return true;
    }).map((item, index) => // eslint-disable-next-line react/jsx-indent
    jsx(Item, _extends({
      key: item.id
    }, getItemProps({
      item,
      index,
      isActive: highlightedIndex === index,
      isSelected: selectedItem === item
    })), itemToString(item))) : null)));
  }));
};

const onAttention = '&:hover, &:focus';
const Item = styled.li({
  position: 'relative',
  cursor: 'pointer',
  display: 'block',
  border: 'none',
  height: 'auto',
  textAlign: 'left',
  borderTop: 'none',
  lineHeight: '1em',
  color: 'rgba(0,0,0,.87)',
  fontSize: '1rem',
  textTransform: 'none',
  fontWeight: '400',
  boxShadow: 'none',
  padding: '.8rem 1.1rem',
  whiteSpace: 'normal',
  wordWrap: 'normal'
}, ({
  isActive,
  isSelected
}) => {
  const styles = [];

  if (isActive) {
    styles.push({
      color: 'rgba(0,0,0,.95)',
      background: 'rgba(0,0,0,.03)'
    });
  }

  if (isSelected) {
    styles.push({
      color: 'rgba(0,0,0,.95)',
      fontWeight: '700'
    });
  }

  return styles;
});
const themes = {
  home: {
    alpha: home.cerulean,
    beta: home.cello,
    gamma: home.cadetBlue,
    delta: home.fireEngineRed
  },
  migratoryWebCitizen: {
    alpha: migratoryWebCitizen.pacificBlue,
    beta: migratoryWebCitizen.cello,
    gamma: migratoryWebCitizen.cadetBlue,
    delta: migratoryWebCitizen.fireEngineRed,
    epsilon: migratoryWebCitizen.cello
  },
  sanitarianRegistersWeb: {
    alpha: sanitarianRegistersWeb.pacificBlue,
    beta: sanitarianRegistersWeb.cello,
    gamma: sanitarianRegistersWeb.cadetBlue,
    delta: sanitarianRegistersWeb.fireEngineRed,
    epsilon: sanitarianRegistersWeb.cello
  },
  phytosanitaryWeb: {
    alpha: phytosanitaryWeb.lightSeaGreen,
    beta: phytosanitaryWeb.sherpaBlue,
    gamma: phytosanitaryWeb.mabel,
    delta: phytosanitaryWeb.lightSeaGreen,
    epsilon: phytosanitaryWeb.allports
  },
  tdCarWeb: {
    alpha: tdCarWeb.pacificBlue,
    beta: tdCarWeb.cello,
    gamma: tdCarWeb.cadetBlue,
    delta: tdCarWeb.fireEngineRed,
    epsilon: tdCarWeb.cello
  }
};
const Input = styled.input({
  width: '100%',
  // full width - icon width/2 - border
  boxShadow: '0px 7px 64px rgba(0, 0, 0, 0.07)',
  borderRadius: 6,
  height: 50,
  padding: '14px 16px',
  fontSize: 15,
  color: '#2B2D42',
  fontStyle: 'normal',
  fontWeight: 'normal',
  wordWrap: 'break-word',
  lineHeight: '1em',
  outline: 0,
  whiteSpace: 'normal',
  minHeight: '2em',
  background: '#fff',
  display: 'inline-block',
  transition: 'box-shadow .1s ease,width .1s ease',
  boxSizing: 'border-box',
  [onAttention]: {
    borderColor: '#0883A0',
    boxShadow: '0 2px 3px 0 rgba(34,36,38,.15)',
    color: '#0883a0'
  }
}, ({
  isOpen,
  error,
  touched,
  disabled,
  theme: themeProvider
}) => {
  const {
    theme = 'migratoryWebCitizen'
  } = themeProvider;
  const styles = [];
  styles.push({
    border: '1px solid',
    borderColor: themes[theme].epsilon,
    [onAttention]: {
      borderColor: themes[theme].alpha,
      color: themes[theme].alpha
    }
  });

  if (isOpen) {
    styles.push({
      borderBottomLeftRadius: '0',
      borderBottomRightRadius: '0',
      [onAttention]: {
        boxShadow: 'none'
      }
    });
  }

  if (error && error.length > 0 && touched) {
    styles.push({
      border: '1px solid #d33e43 !important',
      cursor: disabled ? 'not-allowed' : ''
    });
  }

  if (disabled) {
    styles.push({
      color: themes[theme].beta,
      background: themes[theme].gamma,
      [onAttention]: {
        color: themes[theme].beta
      },
      border: `none !important`,
      WebkitTextFillColor: themes[theme].beta
    });
  }

  return styles;
});
const BaseMenu = styled.ul({
  padding: 0,
  marginTop: 0,
  position: 'absolute',
  backgroundColor: 'white',
  width: '100%',
  maxHeight: '20rem',
  overflowY: 'auto',
  overflowX: 'hidden',
  outline: 'none',
  transition: 'opacity .1s ease',
  borderRadius: '0 0 .28571429rem .28571429rem',
  boxShadow: '0 2px 3px 0 rgba(34,36,38,.15)',
  borderColor: '#96c8da',
  borderTopWidth: '0',
  borderRightWidth: 1,
  borderBottomWidth: 1,
  borderLeftWidth: 1,
  borderStyle: 'solid',
  'z-index': 1
}, ({
  isOpen
}) => ({
  border: isOpen ? null : 'none'
}));
const Menu = /*#__PURE__*/React.forwardRef((props, ref) => jsx(BaseMenu, _extends({
  ref: ref
}, props)));
const ControllerButton = styled.button({
  backgroundColor: 'transparent',
  border: 'none',
  position: 'absolute',
  right: 0,
  top: 0,
  width: 47,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  outline: 'none'
}, ({
  disabled
}) => ({
  cursor: disabled ? 'not-allowed' : 'pointer'
}));
/**
 * This component represent a Arrow Icon in the drop down.
 * @param {boolean} isOpen Determine if the dropdown is open or close.
 * @param {boolean} disabled Determine if the style is disabled or enabled.
 * @param {boolean} alternative Determine if the style is the alternative (red) or the normal (blue).
 */

const ArrowIcon = ({
  isOpen,
  disabled,
  alternative
}) => {
  const {
    theme = 'migratoryWebCitizen'
  } = useTheme();
  let fillColor = themes[theme].alpha;

  if (disabled) {
    fillColor = themes[theme].beta;
  }

  if (alternative) {
    fillColor = themes[theme].delta;
  }

  const styles = css`
    outline: none;
    color: ${fillColor};
    transform: ${isOpen ? 'rotate(180deg)' : undefined};
    width: 16px;
    height: 16px;
  `;
  return jsx(CaretDownIcon, {
    css: css(styles)
  });
};
/**
 * This component represent a x icon in the drop down.
 * @param {boolean} disabled Determine if the style is disabled or enabled.
 * @param {boolean} alternative Determine if the style is the alternative (red) or the normal (blue).
 */


const XIcon = ({
  disabled,
  alternative
}) => {
  const {
    theme = 'migratoryWebCitizen'
  } = useTheme();
  let strokeColor = themes[theme].alpha;

  if (disabled) {
    strokeColor = themes[theme].beta;
  }

  if (alternative) {
    strokeColor = themes[theme].delta;
  }

  return jsx("svg", {
    css: css`
        stroke: ${strokeColor};
      `,
    viewBox: "0 0 20 20",
    preserveAspectRatio: "none",
    width: 14,
    fill: "transparent",
    strokeWidth: "3px",
    outline: "none"
  }, jsx("path", {
    d: "M1,1 L19,19"
  }), jsx("path", {
    d: "M19,1 L1,19"
  }));
};

const itemToString = i => i ? i.name : '';

export default DropDownCombo;